import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {Area, AREAS} from "/@lib@/commons/areas.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {IFormJsonisable, MxFormElement, OFormElementInit} from "/@lib@/commons/forms.js";
import {BaseElementAsync} from "/@back@/commons/basis.js";
import {TemplateResult} from "/@res@/litHtml/lib/template-result.js";
import {render} from "/@res@/litHtml/lib/render.js";
import {xhtml} from "/@lib@/commons/utils/htmlLit.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {SEC} from "/@lib@/commons/security.js";
import Reg = REG.Reg;
import FALLBACK_ROLE_NAME = SEC.FALLBACK_ROLE_NAME;

// TODO 7.0 : déplacer roles.tsx dans core

export interface JUserRoles {
	grantedRoles?: string[]
	refusedRoles?: string[]
	inheritedRoles?: string[]
}

/**
 * Configuration des roles sur un noeud.
 * Structure de donnée perisistante
 */
export interface JUserSpecifiedRolesMap {
	[account: string]: {
		/** Configuration des roles ajoutés pour cet account sur ce noeud. */
		allowedRoles?: string[]
		/** Configuration des roles refusés pour cet account sur ce noeud. */
		deniedRoles?: string[]
	}
}

/**
 * Configuration des roles sur un noeud enrichi d'informations supplémentaires
 * pour fournir un environnement d'édition satisfaisant.
 */
export interface JUserRolesMap extends JUserSpecifiedRolesMap {
	[account: string]: {
		/** Configuration des roles ajoutés pour cet account sur ce noeud. */
		allowedRoles?: string[]
		/** Configuration des roles refusés pour cet account sur ce noeud. */
		deniedRoles?: string[]
		/** Roles hérités du contexte de ce noeuds pour cet account indépendamment des allowedRoles et deniedRoles spécifiés. */
		inheritedRoles?: string[]
		/** Flag indiquant que cet account n'existe pas dans la base.*/
		userUnknown?: boolean
	}
}


/**
 * Handler graphique associé à un rôle
 */
export interface IRoleEditUiHandlerContext<ENV = any> extends IRegPointer<ENV>, JUserRoles {
	reg: IReg<ENV>,
	/**
	 * Chaine de caractère libre caractérisant le contexte courant
	 * <br/><i>Exemples : user,role,wsp,space</i>
	 **/
	uiLayerSgn?: string,

	/**   */
	groupCanBeUnchecked?: boolean;

	/** visibilité forcée *dans le contexte grantedRoles inheritedRoles* (ie pas en fonction de la valeur courante)) */
	forceVisibilityIfSetOrInherited?: boolean,
}

/**
 * Area construisant un élément <input>
 */
export class RoleEditUiHandler<C extends IRoleEditUiHandlerContext> extends Area<C, HTMLElement> {

	private _uiLayerSgnPattern: RegExp;
	private _dependsOfRoles: string[];

	/**
	 * @param roleCode : code complet, y compris l'exclusifGroup
	 * 	</br> ex : main:reader, editProps, ...
	 * @param exclusifGroup
	 */
	constructor(public roleCode: string) {
		super(roleCode);
	}

	/**
	 * Affichage conditionné à la validation de la regExp sur la signature du layer courant
	 * @param sgnPattern
	 */
	public setUiLayerSgnPattern(sgnPattern: RegExp): this {
		this._uiLayerSgnPattern = sgnPattern;
		return this;
	}

	/**
	 * Affichage conditionné à la validation de la regExp sur la signature du layer courant
	 * @param roleCode
	 */
	public dependsOnRole(roleCode: string) {
		if (!this._dependsOfRoles) this._dependsOfRoles = [];
		this._dependsOfRoles.push(roleCode);
		return this;
	}

	getLabel(ctx: C): string {
		let label = super.getLabel(ctx)
			|| (ctx.reg as Reg<any>).getRoleProperty(this.roleCode, "title") as string
			|| this.roleCode;
		// Rôle explicitement affecté sur ce niveau : on le montre comme invalide
		if (this.isForcedVisible(ctx) === true && (ctx.grantedRoles && ctx.grantedRoles.includes(this.roleCode) || ctx.refusedRoles && ctx.refusedRoles.includes(this.roleCode)))
			label = `${label} [rôle invalide]`;
		return label;
	}

	getDescription(ctx: C): string {
		let desc = super.getDescription(ctx) || (ctx.reg as Reg<any>).getRoleProperty(this.roleCode, "description") as string;
		// Rôle explicitement affecté sur ce niveau : on le montre comme invalide
		if (this.isForcedVisible(ctx) === true && (ctx.grantedRoles && ctx.grantedRoles.includes(this.roleCode) || ctx.refusedRoles && ctx.refusedRoles.includes(this.roleCode)))
			desc = desc ? `Ce rôle est invalide ou inconnu dans ce contexte.\n${desc}` : "Ce rôle est invalide ou inconnu dans ce contexte.";
		return desc;
	}

	/**
	 * isVisibleBase avec prise en compte de la propriété "forceVisibilityIfSetOrInherited"
	 * @param ctx
	 */
	isVisible(ctx: C): boolean {
		if (this.isForcedVisible(ctx) === true)
			return true;
		// - uiLayerSgnPattern
		if (this._uiLayerSgnPattern) {
			if (!ctx.uiLayerSgn || !this._uiLayerSgnPattern.test(ctx.uiLayerSgn))
				return false;
		}
		// - dependsOrRoles
		if (this._dependsOfRoles) {
			let userRoles = ROLES.resolveUserRoles(ctx, true);
			if (!userRoles || !this._dependsOfRoles.find((entry) => userRoles.indexOf(entry) > -1 ? true : false))
				return false;
		}
		return super.isVisible(ctx);
	}

	/**
	 * Retourne true SSi la visibilité a été forcée.
	 * Remarque : le composant peut néanmoins être invisible pour une autre raison
	 * @param ctx
	 */
	isForcedVisible(ctx: C): true | undefined {
		let resolverRoles = ROLES.resolveUserRoles(ctx, true);
		if (ctx.forceVisibilityIfSetOrInherited && (resolverRoles.includes(this.roleCode) || (ctx.refusedRoles && ctx.refusedRoles.includes(this.roleCode)) || (ctx.grantedRoles && ctx.grantedRoles.includes(this.roleCode)))) {
			// - uiLayerSgnPattern
			if (this._uiLayerSgnPattern) {
				if (!ctx.uiLayerSgn || !this._uiLayerSgnPattern.test(ctx.uiLayerSgn))
					return true;
			}
			// - dependsOrRoles
			if (this._dependsOfRoles) {
				let userRoles = ROLES.resolveUserRoles(ctx, true);
				if (!userRoles || !this._dependsOfRoles.find((entry) => userRoles.indexOf(entry) > -1 ? true : false))
					return true;
			}
		}
		return undefined;
	}

	isEnabled(ctx: C): boolean {
		// Rôle forcé visible, mais non affecté sur ce niveau : disabled
		if (this.isForcedVisible(ctx) === true && !(ctx.grantedRoles && ctx.grantedRoles.includes(this.roleCode) || ctx.refusedRoles && ctx.refusedRoles.includes(this.roleCode)))
			return false;
		let setPerm = (ctx.reg as Reg<any>).getRoleProperty(this.roleCode, "setPerm") as string;
		if (setPerm && !this.checkObjectRootPerm(ctx, setPerm)) return false;
		return super.isEnabled(ctx);
	}

	getGroup(ctx: C): string | null {
		return ROLES.extractGroupFromRole(this.roleCode);
	}

	/** Tag root : label + control */
	buildBody(ctx: C): HTMLElement {
		const control = this._buildControl(ctx);
		this._initControlElement(control, ctx);
		const body = this._buildControlLabel(control, ctx);
		this._initControlLabelElement(body, control, ctx);
		return body;
	}

	/** Control (input) */
	_buildControl(ctx: C): HTMLInputElement & IFormJsonisable {
		let exclusifGroup = this.getGroup(ctx) ? this.getGroup(ctx) + ":" : null;
		let input = <input name={exclusifGroup || this.roleCode} type={exclusifGroup ? "radio" : "checkbox"} value={this.roleCode}/> as HTMLInputElement;
		return input as (HTMLInputElement & IFormJsonisable);
	}

	protected _initControlElement(input: HTMLInputElement & IFormJsonisable, ctx: C) {
		DOM.setHidden(input, !this.isVisible(ctx));
		DOM.setAttrBool(input, "disabled", !this.isEnabled(ctx));

		// - affectation de l'état initial
		if (this.canBeIndeterminate(ctx)) {
			if (ctx.refusedRoles && ctx.refusedRoles.indexOf(this.roleCode) > -1) {
				RoleEditUiHandler.setIndeterminate(input, false);
				RoleEditUiHandler.setInputChecked(input, false);
			} else if (ctx.grantedRoles && ctx.grantedRoles.indexOf(this.roleCode) > -1) {
				RoleEditUiHandler.setIndeterminate(input, false);
				RoleEditUiHandler.setInputChecked(input, true);
			} else {
				let userRoles = ROLES.resolveUserRoles(ctx, true);
				if (ctx.inheritedRoles && ctx.inheritedRoles.indexOf(this.roleCode) > -1 && userRoles.indexOf(this.roleCode) > -1) {
					RoleEditUiHandler.setIndeterminate(input, true);
					RoleEditUiHandler.setInputChecked(input, true);
				}
			}

			/*else if (ctx.inheritedRoles && ctx.inheritedRoles.indexOf(this.roleCode) > -1) {
				RoleEditUiHandler.setIndeterminate(input, true);
				RoleEditUiHandler.setInputChecked(true);
			}*/
		} else {
			if (ctx.grantedRoles && ctx.grantedRoles.indexOf(this.roleCode) > -1)
				RoleEditUiHandler.setInputChecked(input, true);
		}
		// - set "fillJson"
		Object.assign(input, {
			extractJson: function (this: HTMLInputElement, parent: Jsonisable) {
				throw "No extractJson. Used global init object";
			},
			fillJson: function (this: HTMLInputElement, parent: { grantedRoles: string[], refusedRoles: string[] }, root: Jsonisable) {
				throw "No fillJson. Used global init object";
			}
		});

	}

	/** Label */
	protected _buildControlLabel(control: HTMLElement, ctx: C, label = this.getLabel(ctx)): HTMLSpanElement {
		const elemId = this.getId() + '~' + Math.floor(Math.random() * 100000).toString(36);
		control.id = elemId;
		return <span class="ctrlLbl">
			{control}
			<label class="lbl" for={elemId}>{label}</label>
		</span> as HTMLSpanElement;
	}

	/** Retourne true si l'état 'indeterminate' doit être géré par cet input */
	private canBeIndeterminate(ctx: C): boolean {
		if (!ctx.inheritedRoles)
			return false;
		let currentGroup = this.getGroup(ctx);
		if (currentGroup)
			return ctx.inheritedRoles.find((role) => ROLES.extractGroupFromRole(role) === currentGroup) ? true : false;
		else
			return ctx.inheritedRoles.indexOf(this.roleCode) > -1
	}

	protected _initControlLabelElement(ctrl: HTMLSpanElement, input: HTMLInputElement, ctx: C) {
		ctrl.setAttribute('area-id', this.getId());
		if (this.getDescription(ctx))
			ctrl.title = this.getDescription(ctx);
		DOM.setHidden(ctrl, !this.isVisible(ctx));
		DOM.setAttrBool(ctrl, "disabled", !this.isEnabled(ctx));
		// Rôle explicitement affecté sur ce niveau
		DOM.setAttrBool(ctrl, "data-forced-vivibility-currentlevel", this.isForcedVisible(ctx) === true && (ctx.grantedRoles && ctx.grantedRoles.includes(this.roleCode) || ctx.refusedRoles && ctx.refusedRoles.includes(this.roleCode)));
		DOM.setAttrBool(ctrl, "data-forced-vivibility", this.isForcedVisible(ctx));
		// Role explicitement désactivé à ce niveau
		DOM.setAttrBool(ctrl, "data-forced-refused", ctx.refusedRoles && ctx.refusedRoles.includes(this.roleCode) && !this.getGroup(ctx));

		const canBeIndeterminate = this.canBeIndeterminate(ctx);

		ctrl.addEventListener("click", function (ev: Event) {
			if (!input.readOnly && !input.disabled) {
				let currentGoup = ROLES.extractGroupFromRole(input.value);
				// Gestion du troisème état "indeterminate" (non géré nativement sur les radio)
				if (canBeIndeterminate) {
					if (RoleEditUiHandler.isIndeterminate(input)) {
						RoleEditUiHandler.setInputChecked(input, false);
						RoleEditUiHandler.setIndeterminate(input, false);
					} else if (RoleEditUiHandler.isInputChecked(input)) {
						RoleEditUiHandler.setInputChecked(input, false);
						RoleEditUiHandler.setIndeterminate(input, true);
					} else {
						RoleEditUiHandler.setInputChecked(input, true);
						RoleEditUiHandler.setIndeterminate(input, false);
					}
					ev.stopPropagation();
					ev.preventDefault();
					input.dispatchEvent(new CustomEvent('change', {bubbles: true, composed: true}));
				} else {
					RoleEditUiHandler.setInputChecked(input, input.checked);
				}
			}
		});

		ctrl.addEventListener("input", function (ev) {
			ev.stopPropagation();
			ev.preventDefault();
		});
		ctrl.addEventListener("change", function (this: HTMLInputElement, ev: Event) {
			if (!ctx.grantedRoles) ctx.grantedRoles = [];
			if (!ctx.refusedRoles) ctx.refusedRoles = [];
			//console.log("BEFORE:::", ctx.grantedRoles, ctx.refusedRoles);
			let currentGoup = ROLES.extractGroupFromRole(this.value);
			if (currentGoup) {
				ctx.grantedRoles = ctx.grantedRoles.filter((value) => !value.startsWith(currentGoup + ":"));
				ctx.refusedRoles = ctx.refusedRoles.filter((value) => !value.startsWith(currentGoup + ":"));
			}
			if (RoleEditUiHandler.isIndeterminate(this) && !RoleEditUiHandler.isInputChecked(this)) {
				// indeterminé => clean du role courant
				ctx.grantedRoles = ctx.grantedRoles.filter((value) => value !== this.value);
				ctx.refusedRoles = ctx.refusedRoles.filter((value) => value !== this.value);
			} else {
				RoleEditUiHandler.setIndeterminate(this, false);
				if (ctx.inheritedRoles && ctx.inheritedRoles.indexOf(this.value) > -1 && !RoleEditUiHandler.isInputChecked(this)) {
					ctx.grantedRoles = ctx.grantedRoles.filter((value) => value !== this.value);
					if (!currentGoup || ctx.groupCanBeUnchecked) ctx.refusedRoles.push(this.value);
				} else if (RoleEditUiHandler.isInputChecked(this)) {
					ctx.refusedRoles = ctx.refusedRoles.filter((value) => value !== this.value);
					ctx.grantedRoles.push(this.value);
				} else if (!RoleEditUiHandler.isInputChecked(this)) {
					ctx.grantedRoles = ctx.grantedRoles.filter((value) => value !== this.value);
					ctx.refusedRoles = ctx.refusedRoles.filter((value) => value !== this.value);
				}
			}
			//console.log("AFTER:::", ctx.grantedRoles, ctx.refusedRoles);
			RoleEditUiHandler.dispatchChangeEvent(this);
			ev.stopPropagation();
		}.bind(input));

		if (this.canBeIndeterminate(ctx)) {
			// Pas de valeur saisie pour le groupe
			let currentGoup = ROLES.extractGroupFromRole(this.roleCode);
			if (currentGoup) {
				let groupIsSet = false;
				if (ctx.grantedRoles && ctx.grantedRoles.find((entry) => entry.startsWith(currentGoup + ":")))
					groupIsSet = true;
				else if (ctx.refusedRoles && ctx.refusedRoles.find((entry) => entry.startsWith(currentGoup + ":")))
					groupIsSet = true;
				if (!groupIsSet) {
					DOM.setAttrBool(ctrl, "data-group-indeterminate-state", true);
					RoleEditUiHandler.setIndeterminate(ctrl, RoleEditUiHandler.isIndeterminate(input));
				}
			} else {
				if (RoleEditUiHandler.isIndeterminate(input)) {
					DOM.setAttrBool(ctrl, "data-group-indeterminate-state", true);
					RoleEditUiHandler.setIndeterminate(ctrl, RoleEditUiHandler.isIndeterminate(input));
				}
			}
		}
	}

	static dispatchChangeEvent(elt: HTMLElement) {
		let ev = new CustomEvent('roles-select-change', {bubbles: true, composed: true});
		elt.dispatchEvent(ev);
	}


	static isIndeterminate(ctrl: HTMLElement): boolean {
		return ctrl.hasAttribute("data-is-indeterminate-checked");
	}

	static setIndeterminate(ctrl: HTMLElement, value: boolean): void {
		DOM.setAttrBool(ctrl, "data-is-indeterminate-checked", value);
		if (value)
			ctrl.title = 'Rôle affecté dans un autre niveau (groupes, ...).';
		else
			ctrl.title = "";
	}

	static isInputChecked(ctrl: HTMLInputElement): boolean {
		return ctrl.hasAttribute("data-is-checked");
	}

	static setInputChecked(ctrl: HTMLInputElement, value: boolean): void {
		DOM.setAttrBool(ctrl, "data-is-checked", value);
		ctrl.checked = value;
	}


}


/**
 * Input de sélection des rôles
 * FIXME : ajouter un mode permissif permettant d'afficher aussi les rôles inconnus de "rolesLists"
 */
interface ORolesListSet {
	/** Codes des listes d'area fournissant les {@link RoleEditUiHandler} */
	rolesLists: string[]
}

export interface ORolesSelectorInit extends OFormElementInit, ORolesListSet {
	reg: IReg<any>
	/** Surcharge de la zone de build de layout.
	 * 		DOIT posséder les points d'injection des areas des {@link RoleEditUiHandler}  via @area-ids et @area-groups
	 */
	rolesLayout?: (ctx: ORolesListSet) => TemplateResult

	/** Supprime les fieldset sans contenu */
	autocleanEmptyFieldset?: boolean,

	/** */
	canBeIndeterminate?: boolean,
}

export class RolesSelectorInput extends MxFormElement(BaseElementAsync) {
	reg: IReg<any>;

	protected _params: ORolesSelectorInit & IRoleEditUiHandlerContext;
	protected _form: HTMLFormElement;

	protected async _initialize(init: ORolesSelectorInit & IRoleEditUiHandlerContext): Promise<any> {
		this._params = Object.assign({
			rolesLists: [ROLES.UI_ROLES_LIST],
			autocleanEmptyFieldset: true,
			forceVisibilityIfSetOrInherited: true,
			groupCanBeUnchecked: true,
		}, init);
		this.reg = this.findReg(init);
		this._form = <form/> as HTMLFormElement;

		let onFormUpdateRequest = 0;
		this._form.addEventListener('roles-select-change', (ev) => {
			if (onFormUpdateRequest) {
				clearTimeout(onFormUpdateRequest);
				onFormUpdateRequest = 0;
			}
			onFormUpdateRequest = setTimeout(async () => {
				onFormUpdateRequest = 0;
				await this.onCtxChange();
			}, 100) as any;
		});

		this._attach(this.localName, init, this._form);
		//await render(await this.rolesLayout(this._params), this._form);
		await this._refresh();

		this.addEventListener('keydown', async (ev: KeyboardEvent) => {
			if (!this.disabled && (ev.key === 'Delete' || ev.key === 'Backspace') && this === DOMSH.findDocumentOrShadowRoot(this).activeElement) {
				await this.setRolesValues(this._params.canBeIndeterminate ? {} : {refusedRoles: [], grantedRoles: []});
				ev.stopPropagation();
				return true;
			}
			return false;
		});
		this._initializeForm(init);
	}

	// MxFormElement
	get value(): string[] {throw "ERROR : use extractJson/fillJson instead"}

	set value(vals: string[]) {throw "ERROR : use extractJson/fillJson instead"}

	set disabled(val: boolean) {
		super.disabled = val;
		this.refreshDisabledUi();
	}

	get disabled(): boolean {
		return super.disabled;
	}

	/** Affectation UI de l'état disabled sans modifier la propriété "disabled" courante */
	private refreshDisabledUi(): void {
		const isDisabled = this.disabled === true || this._disabledForcedByContext === true;
		let fieldsetList = this._form.querySelectorAll("fieldset");
		fieldsetList.forEach((fieldset) => {
			fieldset.disabled = isDisabled;
		});
	}

	set indeterminate(val: boolean) {
		DOM.setAttrBool(this, "indeterminate", val);
	}

	get indeterminate(): boolean {
		return this.hasAttribute("indeterminate");
	}

	/** Affectation d'une nouvelle valeur refusedRoles/grantedRoles dans le formulaire */
	private async setRolesValues(vals: { refusedRoles?: string[], grantedRoles?: string[], } | null): Promise<void> {
		let params = {refusedRoles: vals.refusedRoles ? vals.refusedRoles.slice() : null, grantedRoles: vals.grantedRoles ? vals.grantedRoles.slice() : null};
		Object.assign(this._params, params);
		return this.onCtxChange();
	}

	/** Affectation d'une nouvelle valeur inheritedRoles */
	async setInheritedRoles(value: string[]): Promise<void> {
		this._params.inheritedRoles = value;
		return this.onCtxChange();
	}

	/** Gestion du layout d'organisation des rôles */
	protected async rolesLayout(ctx: ORolesListSet): Promise<TemplateResult> {
		if (this._params.rolesLayout)
			return this._params.rolesLayout(ctx);
		else if (this.reg.getSvc("roles.sets.tpl")) {
			return (this.reg.getSvc("roles.sets.tpl") as (ctx: ORolesListSet) => TemplateResult)(ctx);
		} else {
			let areasList = await Promise.all(this.reg.mergeLists(...this._params.rolesLists)) as RoleEditUiHandler<IRoleEditUiHandlerContext>[];
			let groupsSet: Set<string> = new Set<string>();
			let result: HTMLElement[] = [];
			areasList.forEach((area) => {
				if (area.isVisible(this._params)) {
					if (area.getGroup(this._params)) groupsSet.add(area.getGroup(this._params));
				}
			});
			groupsSet.forEach((groupCode) => {
				result.push(<fieldset id={groupCode + ":"} area-groups={groupCode} data-bypass/>);
			});
			result.push(<fieldset id="other" area-ids="*" class="subLevel" data-bypass/>);
			return xhtml`${result}`;
		}
	}

	protected async _refresh(): Promise<void> {
		try {
			this.refreshFreeze(1);
			super._refresh();
			await this.onCtxChange();
		} finally {
			this.refreshFreeze(-1);
		}
	}

	/**
	 * Modification d'un élément du contexte des areas => redraw des areas
	 */
	private async onCtxChange(): Promise<void> {
		await render(await this.rolesLayout(this._params), this._form);
		await AREAS.applyLayout(this._form, await Promise.all(this.reg.mergeLists(...this._params.rolesLists)) as RoleEditUiHandler<IRoleEditUiHandlerContext>[], this._params, true);

		// - Clean des fieldset vides
		if (this._params.autocleanEmptyFieldset) {
			let fieldsetList = this._form.querySelectorAll("fieldset");
			fieldsetList.forEach((fieldset) => {
				DOM.setHidden(fieldset, !fieldset.querySelector("input:not([hidden])"));
			});
		}

		this.refreshDisabledUi();

		if (this._params.canBeIndeterminate && !(this._params.grantedRoles || this._params.refusedRoles)) {
			this.indeterminate = true;
		} else
			this.indeterminate = false;

		this.dispatchEvent(new CustomEvent("change", {bubbles: true, cancelable: false}));
		this.dispatchEvent(new CustomEvent("input", {bubbles: true, cancelable: false}));
	}

	private _setValuePending: JUserRoles | undefined = undefined;

	extractJson(parent: Dict<any>): boolean {
		this._setValuePending = parent;
		this.setRolesValues(parent).then(async () => {
			await this.setInheritedRoles(parent["inheritedRoles"]);
			this._setValuePending = undefined;
		});
		return true;
	}

	fillJson(parent: any, root: any): void {
		let roles: { grantedRoles: string[], refusedRoles: string[] } = this._setValuePending ?
			{grantedRoles: this._setValuePending.grantedRoles ? this._setValuePending.grantedRoles.sort() : undefined, refusedRoles: this._setValuePending.refusedRoles ? this._setValuePending.refusedRoles.sort() : undefined} :
			{grantedRoles: this._params.grantedRoles ? this._params.grantedRoles.sort() : undefined, refusedRoles: this._params.refusedRoles ? this._params.refusedRoles.sort() : undefined};
		Object.assign(parent, roles);
	}

	private _disabledForcedByContext = false;

	formDisabledCallback(disabled: boolean) {
		this._disabledForcedByContext = disabled;
		this.refreshDisabledUi();
	}
}

REG.reg.registerSkin('c-input-roles-selector', 1, /* language=CSS */ `
	:host {
		background-color: var(--form-bgcolor);
		border: 1px solid var(--border-color);
	}

	:host(:disabled) {
		background-color: transparent;
	}

	:host([indeterminate]) {
		opacity: .5;
	}

	fieldset > legend {
		letter-spacing: .1rem;
	}

	fieldset {
		border: 1px solid var(--alt1-border-color);
		border-radius: 5px;
		padding: .3rem;
		margin-block-end: .5rem;
		margin-block-start: .1rem;
	}

	fieldset[data-bypass] {
		border: none;
	}

	fieldset[disabled] {
		opacity: .5;
	}

	fieldset[hidden] {
		display: none;
	}

	.subLevel {
		margin-inline-start: 2rem;
	}

	.ctrlLbl {
		display: inline-flex;
		margin-inline-end: 1rem;
		padding: 1px;
	}


	.ctrlLbl[data-is-indeterminate-checked] {
		background-color: var(--row-inSel-unfocus-bgcolor);
		border-radius: 5px;

	}

	.ctrlLbl[data-group-indeterminate-state] {
		/*font-style: italic;*/
		opacity: .6;
	}

	.ctrlLbl[data-forced-vivibility-currentlevel] {
		color: var(--warning-color);
	}

	.ctrlLbl[data-forced-refused] {
		text-decoration: line-through;
	}

	.ctrlLbl[hidden] {
		display: none;
	}

	.ctrlLbl[disabled] {
		opacity: .6;
	}

	.lbl {
		margin-inline-end: .5rem;
		user-select: none;
	}

`);

customElements.define('c-input-roles-selector', RolesSelectorInput);

REG.reg.addToList("user:roles", FALLBACK_ROLE_NAME, 1, new RoleEditUiHandler(FALLBACK_ROLE_NAME)
	.setLabel("Rôle non défini")
	.setVisible(false));


export namespace ROLES {

	/** Groupe de rôles "main" */
	export const GROUP_ROLES_MAIN = "main";

	export const UI_ROLES_LIST = "user:roles";

	/** Retourne le code du groupe, ou null si le role n'appartient pas à un groupe */
	export function extractGroupFromRole(code: string): string | null {
		if (!code) return null;
		return code.indexOf(":") > -1 ? code.substring(0, code.indexOf(":")) : null;
	}

	/** Retourne le label d'un goupe donné
	 * FIXME : concept non modélisé. A faire évoluer...
	 */
	export function findGroupDef(code: string): { label: string, color?: string } | null {
		if (!code) return null;
		switch (code) {
			case GROUP_ROLES_MAIN :
				return {label: "principal", color: "var(--inv-bgcolor)"}
			default:
				return {label: code, color: "var(--inv-bgcolor)"};
		}
	}

	/**
	 * Construit les roles d'un user en combinant JUser.inheritedRoles, JUser.refusedRoles et JUser.grantedRoles,
	 * dans un contexte*avec* gestion de rôles
	 */
	export function resolveUserRoles(userRoles: JUserRoles, disabledFallback: boolean = false): string[] {
		if (userRoles.inheritedRoles) {
			let roles: string[];
			roles = userRoles.refusedRoles ? userRoles.inheritedRoles.filter((r) => userRoles.refusedRoles.indexOf(r) === -1) : userRoles.inheritedRoles.concat();
			if (!userRoles.grantedRoles) return roles;
			userRoles.grantedRoles.forEach((r) => {
				const sep = r.indexOf(':');
				if (sep > 0) {
					const categ = r.substring(0, sep + 1);
					roles = roles.filter((r2) => !r2.startsWith(categ));
					roles.push(r);
				} else {
					if (roles.indexOf(r) === -1) roles.push(r);
				}
			});
			return disabledFallback ? roles : SEC.injectFallbackRole(roles);
		}
		if (disabledFallback)
			return userRoles.grantedRoles || [];
		else
			return SEC.injectFallbackRole(Array.from(userRoles.grantedRoles || []))
	}

	/**
	 * Résout une liste de rôles pour un contexte donné en vue de leur affichage
	 */
	export async function resolveRolesList(roles: string[], ctx: IRoleEditUiHandlerContext, rolesUiHandlers?: RoleEditUiHandler<IRoleEditUiHandlerContext>[]): Promise<{ role: string, sortUi: number, sortAlgo: number, name: string, level: "set" | "inherited" | "unknown" }[]> {
		if (!rolesUiHandlers)
			rolesUiHandlers = await Promise.all(ctx.reg.mergeLists(ROLES.UI_ROLES_LIST)) as RoleEditUiHandler<IRoleEditUiHandlerContext>[];
		let result: { role: string, sortUi: number, sortAlgo: number, name: string, level: "set" | "inherited" | "unknown" }[] = [];
		roles.forEach((role) => {
			let currentRoleUiIndex = rolesUiHandlers.findIndex((roleUi) => roleUi.roleCode === role);
			let currentRoleUi = currentRoleUiIndex > -1 ? rolesUiHandlers[currentRoleUiIndex] : null;
			// On n'affiche que les roles qui ont une existence UI ET ceux inconnus du reg
			// Les rights sont donc masqués
			if (currentRoleUi || (ctx.reg as Reg<any>).getRoleProperty(role, "priority") !== undefined) {
				result.push({
					role,
					name: (currentRoleUi && currentRoleUi.getLabel(ctx)) || (ctx.reg as Reg<any>).getRoleProperty(role, "title") as string || role,
					sortUi: currentRoleUiIndex || Infinity,
					sortAlgo: (ctx.reg as Reg<any>).getRoleProperty(role, "priority") as number,
					level: currentRoleUi ? ((ctx.grantedRoles && ctx.grantedRoles.includes(role)) ? "set" : "inherited") : "unknown",
				});
			}
		});
		// Tri UI : roles main en premier, puis tri par ordre croissant du sortKey
		result.sort((l1, l2) => {
			const g1 = extractGroupFromRole(l1.role);
			const g2 = extractGroupFromRole(l2.role);
			if (g1 === GROUP_ROLES_MAIN && g2 !== GROUP_ROLES_MAIN) return -1;
			if (g1 !== GROUP_ROLES_MAIN && g2 === GROUP_ROLES_MAIN) return 1;
			return l1.sortUi - l2.sortUi;
		});

		return result;
	}

}